import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { environment } from '../../../../environments/environment';

export interface WarehouseOption {
  storeId: string | null;
  locationId: string;
  storeCode?: string | null;
  locationType?: string | null;
  name: string;
}

/**
 * Read-only indicator of the store/warehouse a document belongs to.
 *
 * Deliberately NOT a picker: the working store/warehouse is switched in ONE
 * place — the header switcher — so a document can never disagree with the
 * context the user believes they are in (a second selector here would let a
 * GRN receive stock somewhere other than the location shown at the top).
 * This just shows which context the document will be filed under, and tells
 * the host screen its storeId/locationId.
 *
 * Shows `initialLocationId` when given (a saved document keeps ITS OWN
 * location, even if the viewer is working elsewhere); otherwise the active
 * location from the header.
 */
@Component({
  selector: 'app-warehouse-select',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (resolved) {
      <div class="ws-static" [title]="resolved.name">
        <i class="bi" [class.bi-box-seam]="isWarehouse" [class.bi-shop]="!isWarehouse"></i>
        {{ resolved.name }}
      </div>
    } @else {
      <div class="ws-static ws-static--empty">{{ emptyMessage }}</div>
    }
  `,
  styles: [
    `
      .ws-static {
        padding: 0.375rem 0.25rem;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .ws-static i {
        margin-right: 0.35rem;
        opacity: 0.7;
      }
      .ws-static--empty {
        color: #dc3545;
        font-weight: 400;
        white-space: normal;
      }
    `,
  ],
})
export class WarehouseSelectComponent implements OnInit, OnChanges {
  private authService = inject(AuthService);
  private http = inject(HttpClient);

  /**
   * Show this location instead of the active one — a saved document's own
   * store/warehouse, or the store of a PO a GRN is being received against.
   */
  @Input() initialLocationId: string | null = null;

  /**
   * Location types this flow accepts. Default is both: documents follow
   * whatever the header is set to. Pass ['Warehouse'] for flows that only
   * make sense from a warehouse (e.g. generating replenishment POs) — the
   * field then asks the user to switch at the top instead of silently
   * filing the document against a store.
   */
  @Input() locationTypes: Array<'Warehouse' | 'Store'> = ['Warehouse', 'Store'];

  @Output() selectionChange = new EventEmitter<WarehouseOption | null>();

  resolved: WarehouseOption | null = null;
  emptyMessage = 'No store/warehouse selected';

  get isWarehouse(): boolean {
    return this.resolved?.locationType === 'Warehouse';
  }

  ngOnInit(): void {
    this.resolve();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The host may learn the document's location later (e.g. after a PO or a
    // saved GRN loads), so re-resolve rather than staying on the active one.
    if (changes['initialLocationId'] && !changes['initialLocationId'].firstChange) {
      this.resolve();
    }
  }

  private resolve(): void {
    const access = this.authService.getStoreAccess();
    if (access.length) {
      this.pick(
        access.map((a) => ({
          storeId: a.storeId ?? null,
          locationId: a.locationId,
          storeCode: a.storeCode ?? null,
          locationType: a.locationType ?? null,
          name: a.locationName || a.storeName || a.storeCode || a.locationId,
        })),
      );
      return;
    }

    // Legacy-unrestricted users / admins without access rows: resolve against
    // the full tenant list, joined so the location still carries its storeId.
    forkJoin({
      locations: this.http
        .get<any>(environment.locations, {
          params: new HttpParams().set('isActive', true),
        })
        .pipe(catchError(() => of([]))),
      stores: this.http
        .get<any>(`${environment.apiUrl}/stores`)
        .pipe(catchError(() => of([]))),
    }).subscribe(({ locations, stores }) => {
      const locationList: any[] = locations?.data || locations || [];
      const storeList: any[] = stores?.data || stores || [];
      const storeByLocation = new Map<string, any>(
        storeList.map((s) => [s.locationId, s]),
      );
      this.pick(
        locationList.map((l) => ({
          storeId: storeByLocation.get(l.id)?.id ?? null,
          locationId: l.id,
          storeCode: storeByLocation.get(l.id)?.storeCode ?? null,
          locationType: l.type ?? null,
          name: l.name,
        })),
      );
    });
  }

  private pick(candidates: WarehouseOption[]): void {
    const wanted = new Set<string>(this.locationTypes);
    const targetId =
      this.initialLocationId || this.authService.getActiveLocationId();

    let match =
      candidates.find((c) => c.locationId === targetId) ??
      // Nothing to disambiguate: a single accessible location IS the context.
      (candidates.length === 1 ? candidates[0] : null);

    if (match && match.locationType && !wanted.has(match.locationType)) {
      // Right location, wrong kind for this flow (e.g. raising replenishment
      // POs while working as a store).
      this.emptyMessage = `Switch to a ${this.locationTypes.join(' / ').toLowerCase()} at the top to continue`;
      match = null;
    } else if (!match) {
      this.emptyMessage = 'Select a store/warehouse at the top';
    }

    this.resolved = match;
    this.selectionChange.emit(match);
  }
}
